'use client';

import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { Card } from '@/src/shared/ui/Card';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { getCategoryImageUrl } from '@/src/entities/category/lib';

/**
 * Subcategory item with localized name and stable slug for URL
 */
export interface SubcategoryItem {
  name: string;  // Localized display name
  slug: string;  // URL-safe slug (stable identifier)
}

interface CategoryCardFullProps {
  icon?: string | null;
  imageUrl?: string | null;
  index?: number;
  categoryId?: string;
  categoryName?: string;
  categorySlug?: string;  // Stable slug for URL params
  subcategories?: SubcategoryItem[];
}

export const CategoryCardFull = ({
  icon = '/images/categories/handyman-home-services.png',
  imageUrl: imageUrlProp,
  index = 0,
  categoryId,
  categoryName = 'Handyman & Home Services',
  categorySlug = 'handyman-home-services',
  subcategories = [],
}: CategoryCardFullProps) => {
  const router = useRouter();
  const locale = useLocale();
  const isEven = index % 2 === 0;
  const mobileAlignmentClass = isEven ? 'items-start md:items-start' : 'items-end md:items-start';
  const mobileTextAlignClass = isEven ? 'text-left md:text-left' : 'text-right md:text-left';

  // Get the appropriate image URL for this category (imageUrlProp from server has highest priority)
  const imageUrl = getCategoryImageUrl(icon, categorySlug, imageUrlProp);

  // The category title opens the same map view without a subcategory filter,
  // so the user sees every order in the category.
  const handleCategoryClick = () => {
    const params = new URLSearchParams({ category: categorySlug });
    router.push(`/${locale}/categories/map?${params.toString()}`);
  };

  const handleSubcategoryClick = (subcategory: SubcategoryItem) => {
    // Use slug instead of name for stable URL parameters
    const params = new URLSearchParams({
      category: categorySlug,
      subcategory: subcategory.slug,
    });
    router.push(`/${locale}/categories/map?${params.toString()}`);
  };

  return (
    <Card
      className={`flex flex-col gap-lg p-0 bg-transparent shadow-none ${mobileAlignmentClass}`}
      padding="none"
    >
      <div className={`category-image-circle ${isEven ? 'self-start md:self-auto' : 'self-end md:self-auto'}`}>
        {/* next/image если SSR — <Image ... /> */}
        <img
          src={imageUrl}
          alt={categoryName}
          draggable={false}
          loading="lazy"
        />
      </div>
      <button
        type="button"
        onClick={handleCategoryClick}
        className={`mb-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-70 transition-opacity ${mobileTextAlignClass}`}
      >
        <Typography variant="h2" weight="bold">
          {categoryName}
        </Typography>
      </button>
      <div className={`flex flex-col gap-md ${mobileAlignmentClass}`}>
        {subcategories.map((subcategory, idx) => (
          <button
            key={subcategory.slug || idx}
            onClick={() => handleSubcategoryClick(subcategory)}
            className="btn-link"
          >
            <Typography color="accent" variant="body" weight="medium" align="left">
              {subcategory.name}
            </Typography>
          </button>
        ))}
      </div>
    </Card>
  );
};